import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AdDto } from "@ads/shared";
import { bulkAdAction, fetchAds, setAdStatus } from "../api/ads.js";
import { fetchOrganisation } from "../api/organisations.js";
import { StatusToggle } from "../components/StatusToggle.js";
import { BulkBar, type BulkAction } from "../components/BulkBar.js";

const PAGE_SIZE = 25;

/** A sortable column header (col keys must match the backend AD_SORT allow-list). */
function SortTh({
  label,
  col,
  sort,
  order,
  onSort,
}: {
  label: string;
  col: string;
  sort: string;
  order: "asc" | "desc";
  onSort: (col: string) => void;
}) {
  return (
    <th>
      <button type="button" className="sort-th" onClick={() => onSort(col)} aria-label={`Sort by ${label}`}>
        {label}
        {sort === col ? (order === "asc" ? " ▲" : " ▼") : ""}
      </button>
    </th>
  );
}

function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Default-ad listing for one org (US-C1/C2, US-F1/F2). */
export function AdListPage() {
  const { orgId = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("dateModified");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const q = useDebounced(search.trim());

  useEffect(() => setPage(1), [q, sort, order]);

  const list = useQuery({
    queryKey: ["ads", orgId, { q, page, sort, order }],
    queryFn: () => fetchAds(orgId, { q: q || undefined, page, limit: PAGE_SIZE, sort, order }),
    placeholderData: keepPreviousData,
  });

  // The org whose ads these are — its name/custId head the page (matches the picker label).
  const org = useQuery({
    queryKey: ["organisation", orgId],
    queryFn: () => fetchOrganisation(orgId),
    enabled: !!orgId,
  });

  const items = useMemo<AdDto[]>(() => list.data?.items ?? [], [list.data]);
  const total = list.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const invalidate = () => qc.invalidateQueries({ queryKey: ["ads", orgId] });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 0 | 1 }) => setAdStatus(id, status),
    onSuccess: invalidate,
  });
  const bulkMut = useMutation({
    mutationFn: (action: BulkAction) => bulkAdAction({ ids: [...selected], action }),
    onSuccess: () => {
      setSelected(new Set());
      return invalidate();
    },
  });

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  const allSelected = items.length > 0 && items.every((a) => selected.has(a.id));
  function toggleAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) items.forEach((a) => next.delete(a.id));
      else items.forEach((a) => next.add(a.id));
      return next;
    });
  }

  function onSort(key: string) {
    if (sort === key) setOrder((o) => (o === "asc" ? "desc" : "asc"));
    else {
      setSort(key);
      setOrder("asc");
    }
  }

  function onBulk(action: BulkAction) {
    if (action === "delete" && !window.confirm(`Delete ${selected.size} selected default ad(s)?`)) return;
    bulkMut.mutate(action);
  }

  return (
    <main>
      <div className="page-head">
        <Link to="/" className="back-link">← Organisations</Link>
        <button
          type="button"
          className="btn primary"
          onClick={() => navigate(`/orgs/${orgId}/ads/new`, { state: { orgIds: [orgId] } })}
        >
          New default ad
        </button>
      </div>

      <h1 className="org-title">
        {org.data ? `${org.data.name} [${org.data.custId}]` : org.isLoading ? "Loading…" : "Default ads"}
      </h1>

      <div className="list-toolbar">
        <input
          className="search-input"
          placeholder="Search ads by title"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search ads"
        />
      </div>

      {selected.size > 0 && <BulkBar count={selected.size} busy={bulkMut.isPending} onAction={onBulk} />}

      <table className="ads-table">
        <thead>
          <tr>
            <th className="col-check">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAllVisible}
                aria-label="Select all on this page"
              />
            </th>
            <SortTh label="Title" col="title" sort={sort} order={order} onSort={onSort} />
            <SortTh label="Start" col="startDate" sort={sort} order={order} onSort={onSort} />
            <SortTh label="End" col="endDate" sort={sort} order={order} onSort={onSort} />
            <SortTh label="Status" col="status" sort={sort} order={order} onSort={onSort} />
            <th>Position</th>
            <th>Link</th>
            <SortTh label="Modified" col="dateModified" sort={sort} order={order} onSort={onSort} />
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {list.isError && (
            <tr>
              <td colSpan={9} className="error" role="alert">Could not load ads.</td>
            </tr>
          )}
          {!list.isError && items.length === 0 && (
            <tr>
              <td colSpan={9} className="empty">{list.isLoading ? "Loading…" : "No default ads yet."}</td>
            </tr>
          )}
          {items.map((ad) => (
            <tr key={ad.id}>
              <td className="col-check">
                <input
                  type="checkbox"
                  checked={selected.has(ad.id)}
                  onChange={() => toggleOne(ad.id)}
                  aria-label={`Select ${ad.title}`}
                />
              </td>
              <td className="col-title">{ad.title || <span className="muted-note">(untitled)</span>}</td>
              <td>{ad.startDate}</td>
              <td>{ad.endDate}</td>
              <td>
                <StatusToggle
                  status={ad.status}
                  busy={statusMut.isPending}
                  onToggle={(next) => statusMut.mutate({ id: ad.id, status: next })}
                />
              </td>
              <td>{cap(ad.position)}</td>
              <td className="col-link">
                {ad.linkType === "email" ? `mailto: ${ad.linkValue}` : ad.linkValue}
              </td>
              <td>{ad.dateModified.slice(0, 10)}</td>
              <td>
                <Link className="row-edit" to={`/ads/${ad.id}/edit`}>Edit</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <nav className="list-foot" aria-label="Pagination">
        <span className="muted-note">{total} ad{total === 1 ? "" : "s"}</span>
        <div className="pager-simple">
          <button type="button" className="btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span>Page {page} of {lastPage}</span>
          <button type="button" className="btn" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      </nav>
    </main>
  );
}
